import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SupportResources = () => {
  const resources = [
    {
      icon: 'BookOpen',
      title: 'API Documentation',
      description: 'Comprehensive guides, tutorials, and reference documentation for all endpoints.',
      link: '#',
      color: 'blue'
    },
    {
      icon: 'Code2',
      title: 'Code Examples',
      description: 'Production-ready code samples in multiple programming languages.',
      link: '#',
      color: 'green'
    },
    {
      icon: 'MessageSquare',
      title: 'Developer Forum',
      description: 'Connect with other developers, ask questions, and share best practices.',
      link: '#',
      color: 'purple'
    },
    {
      icon: 'Headphones',
      title: 'Technical Support',
      description: 'Direct access to our engineering team for enterprise customers.',
      link: '#',
      color: 'red'
    },
    {
      icon: 'Github',
      title: 'GitHub Repository',
      description: 'Open-source SDKs, examples, and community contributions.',
      link: '#',
      color: 'gray'
    },
    {
      icon: 'Zap',
      title: 'Status Page',
      description: 'Real-time API status, uptime monitoring, and incident reports.',
      link: '#',
      color: 'yellow'
    }
  ];

  const faqs = [
    {
      question: 'How do I get started with the API?',
      answer: 'Sign up for a developer account, generate your API key from the dashboard, and follow our Quick Start Guide. You can make your first API call in under 5 minutes.'
    },
    {
      question: 'What authentication methods are supported?',
      answer: 'We support API key authentication via the Authorization header. Enterprise customers can also use OAuth 2.0 and JWT tokens for enhanced security.'
    },
    {
      question: 'Are there sandbox environments for testing?',
      answer: 'Yes, all accounts have access to a sandbox environment with test data. Use the sandbox API key to test integrations without affecting production data.'
    },
    {
      question: 'How do I handle rate limits?',
      answer: 'Monitor rate limit headers in API responses and implement exponential backoff. Enterprise plans offer higher limits and custom rate limit configurations.'
    },
    {
      question: 'What SLA guarantees do you provide?',
      answer: 'Professional plans include 99.9% uptime SLA. Enterprise plans offer 99.99% uptime with dedicated support and custom SLA terms.'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100',
      green: 'bg-green-100',
      purple: 'bg-purple-100',
      red: 'bg-red-100',
      gray: 'bg-gray-100',
      yellow: 'bg-yellow-100'
    };
    return colors?.[color] || colors?.blue;
  };

  const getIconColor = (color) => {
    const colors = {
      blue: 'var(--color-secondary)',
      green: 'var(--color-success)',
      purple: '#9333EA',
      red: 'var(--color-primary)',
      gray: '#6B7280',
      yellow: 'var(--color-warning)'
    };
    return colors?.[color] || colors?.blue;
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Developer Resources & Support
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to build, test, and deploy integrations with PolicyAlerts API.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {resources?.map((resource, index) => (
            <a
              key={index}
              href={resource?.link}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className={`w-12 h-12 ${getColorClasses(resource?.color)} rounded-lg flex items-center justify-center mb-4`}>
                <Icon name={resource?.icon} size={24} color={getIconColor(resource?.color)} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {resource?.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {resource?.description}
              </p>
              <div className="flex items-center gap-2 text-blue-600 text-sm font-medium">
                <span>Learn more</span>
                <Icon name="ArrowRight" size={16} />
              </div>
            </a>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8 mb-12">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            {faqs?.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                  {faq?.question}
                </h4>
                <p className="text-sm md:text-base text-gray-600">
                  {faq?.answer}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Start Building?
          </h3>
          <p className="text-base md:text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Get your API key and start integrating PolicyAlerts into your platform today. Join hundreds of healthcare technology companies already using our API.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="default" size="lg" className="bg-white text-blue-600 hover:bg-gray-100" iconName="Key" iconPosition="left">
              Get API Key
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" iconName="MessageSquare" iconPosition="left">
              Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportResources;